'use client';

import { useEffect, useRef } from 'react';

const stages = [
  {
    time: '10:00 PM',
    label: 'You Drink',
    desc: 'Natural citrus compounds enter bloodstream',
    color: '#FF6B1A',
    icon: '🥛',
  },
  {
    time: '11:00 PM',
    label: 'Enzymes Activate',
    desc: 'Lipolysis enzymes begin targeting stored fat',
    color: '#FF8C42',
    icon: '⚡',
  },
  {
    time: '1:00 AM',
    label: 'Deep Burn Phase',
    desc: 'Peak fat oxidation — 3× normal resting rate',
    color: '#FFB347',
    icon: '🔥',
  },
  {
    time: '4:00 AM',
    label: 'Sustained Release',
    desc: 'Hesperidin maintains metabolic elevation',
    color: '#FFD080',
    icon: '🌙',
  },
  {
    time: '6:00 AM',
    label: 'Wake Leaner',
    desc: 'Morning cortisol spikes further accelerate results',
    color: '#FFF8F0',
    icon: '☀️',
  },
];

export default function ScienceSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.12 }
    );
    const elements = sectionRef?.current?.querySelectorAll('.reveal, .reveal-left, .reveal-right');
    elements?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      id="science"
      ref={sectionRef}
      className="py-32 px-6 relative overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #0D0F1A 0%, #0F1020 50%, #0D0F1A 100%)' }}
    >
      {/* Background accent */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] rounded-full blur-[150px] pointer-events-none"
        style={{ background: 'radial-gradient(ellipse, rgba(255,107,26,0.06) 0%, transparent 70%)' }}
      />
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-24 reveal">
          <p className="text-citrus font-bold uppercase tracking-[0.3em] text-xs mb-4">The Science</p>
          <h2 className="font-fraunces text-5xl md:text-7xl font-bold tracking-tight leading-[0.9] mb-6">
            What happens
            <br />
            <span className="gradient-text-citrus italic">while you sleep.</span>
          </h2>
          <p className="text-cream-muted text-lg max-w-xl mx-auto leading-relaxed">
            NightBurnOJ's formula leverages the natural synergy between citrus flavonoids and your body's nocturnal repair cycle.
          </p>
        </div>

        {/* Two-column layout: timeline + visual */}
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Timeline */}
          <div className="space-y-6 reveal-left">
            {stages?.map((stage, i) => (
              <div
                key={i}
                className="relative flex gap-5 items-start group"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                {/* Connector line */}
                {i < stages?.length - 1 && (
                  <div className="absolute left-[1.6rem] top-12 w-px h-[calc(100%-8px)]"
                    style={{ background: `linear-gradient(180deg, ${stage?.color}40, transparent)` }}
                  />
                )}
                {/* Icon bubble */}
                <div
                  className="relative z-10 w-12 h-12 shrink-0 rounded-full flex items-center justify-center text-xl border border-white/10 transition-all duration-500 group-hover:scale-110"
                  style={{ background: `linear-gradient(135deg, ${stage?.color}20, ${stage?.color}08)` }}
                >
                  {stage?.icon}
                </div>
                {/* Content */}
                <div className="pt-1">
                  <div className="flex items-center gap-3 mb-1">
                    <span
                      className="text-xs font-bold font-mono uppercase tracking-widest"
                      style={{ color: stage?.color }}
                    >
                      {stage?.time}
                    </span>
                    <span className="text-cream font-semibold text-sm">{stage?.label}</span>
                  </div>
                  <p className="text-muted text-sm leading-relaxed">{stage?.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Visual: Before/After metabolic chart */}
          <div className="reveal-right">
            <div className="glass-card rounded-3xl p-8 relative overflow-hidden">
              {/* Glow */}
              <div className="absolute inset-0 pointer-events-none"
                style={{ background: 'radial-gradient(circle at 50% 100%, rgba(255,107,26,0.1) 0%, transparent 60%)' }}
              />

              <div className="relative z-10">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <p className="text-xs text-muted uppercase tracking-widest font-bold mb-1">Metabolic Rate</p>
                    <p className="text-2xl font-fraunces font-bold text-cream">Overnight Comparison</p>
                  </div>
                  <div className="flex flex-col gap-1.5 text-xs">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-1 rounded-full bg-white/20" />
                      <span className="text-muted">Without</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-1 rounded-full bg-citrus" />
                      <span className="text-cream">With NightBurnOJ</span>
                    </div>
                  </div>
                </div>

                {/* SVG Chart */}
                <div className="relative h-48 w-full mb-4">
                  <svg viewBox="0 0 400 160" className="w-full h-full" preserveAspectRatio="none">
                    {/* Grid */}
                    {[40, 80, 120]?.map((y) => (
                      <line key={y} x1="0" y1={y} x2="400" y2={y} stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
                    ))}

                    {/* Without NightBurn — flat */}
                    <path
                      d="M0,120 C50,118 100,122 150,120 C200,118 250,122 300,120 C350,118 380,120 400,119"
                      fill="none"
                      stroke="rgba(255,255,255,0.15)"
                      strokeWidth="2"
                      strokeDasharray="6 4"
                    />

                    {/* Fill area for NightBurn */}
                    <path
                      d="M0,120 C30,110 60,85 100,60 C140,35 180,25 220,30 C260,35 300,55 340,80 C370,98 390,112 400,115 L400,160 L0,160 Z"
                      fill="url(#burnGradient)"
                      opacity="0.25"
                    />

                    {/* NightBurn curve */}
                    <path
                      d="M0,120 C30,110 60,85 100,60 C140,35 180,25 220,30 C260,35 300,55 340,80 C370,98 390,112 400,115"
                      fill="none"
                      stroke="url(#lineGradient)"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                    />

                    {/* Peak annotation dot */}
                    <circle cx="210" cy="28" r="5" fill="#FF6B1A" />
                    <circle cx="210" cy="28" r="9" fill="rgba(255,107,26,0.25)" />

                    <defs>
                      <linearGradient id="lineGradient" x1="0" y1="0" x2="400" y2="0" gradientUnits="userSpaceOnUse">
                        <stop offset="0%" stopColor="#FF6B1A" />
                        <stop offset="50%" stopColor="#FFB347" />
                        <stop offset="100%" stopColor="#FF6B1A" />
                      </linearGradient>
                      <linearGradient id="burnGradient" x1="0" y1="0" x2="0" y2="1" gradientUnits="objectBoundingBox">
                        <stop offset="0%" stopColor="#FF6B1A" stopOpacity="0.6" />
                        <stop offset="100%" stopColor="#FF6B1A" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                  </svg>

                  {/* Peak label */}
                  <div className="absolute top-2 left-1/2 -translate-x-1/2 glass-card-warm px-3 py-1.5 rounded-full text-xs font-bold text-amber">
                    Peak: 1–3 AM ↑ 340%
                  </div>
                </div>

                {/* Time labels */}
                <div className="flex justify-between text-[10px] text-muted uppercase tracking-widest font-mono">
                  <span>10 PM</span>
                  <span>12 AM</span>
                  <span>2 AM</span>
                  <span>4 AM</span>
                  <span>6 AM</span>
                </div>

                {/* Stats row */}
                <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-white/5">
                  {[
                    { val: '3.4×', label: 'Fat burn rate' },
                    { val: '8 hrs', label: 'Active window' },
                    { val: '420', label: 'Avg. calories' },
                  ]?.map((stat) => (
                    <div key={stat?.label} className="text-center">
                      <p className="text-xl font-fraunces font-bold gradient-text-citrus">{stat?.val}</p>
                      <p className="text-[10px] text-muted mt-0.5">{stat?.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}